import React, { useEffect, useState, useCallback } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Box, CircularProgress, Grid,
    Typography, IconButton, Autocomplete, Divider,
    Paper
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { orderListAPI, supplierAPI } from '../../services/api';
import centralStockAPI from '../../services/centralStockAPI';

// ── Empty item template ───────────────────────────────────────────────────────
const emptyItem = () => ({
    centralStockItem: null,
    quantity: '',
    unitPrice: '',
});

const OrderFormDialog = ({ open, onClose, onSuccess, order }) => {
    const { t } = useTranslation();
    const isEdit = Boolean(order);

    // ── Form state ────────────────────────────────────────────────────────────
    const [supplier, setSupplier] = useState(null);
    const [items, setItems] = useState([emptyItem()]);
    const [paidPrice, setPaidPrice] = useState('');
    const [notes, setNotes] = useState('');
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    // ── Reference data ────────────────────────────────────────────────────────
    const [suppliers, setSuppliers] = useState([]);
    const [stockItems, setStockItems] = useState([]);
    const [refLoading, setRefLoading] = useState(false);

    // ── Derived totals ────────────────────────────────────────────────────────
    const totalPrice = items.reduce((sum, item) => {
        const qty = parseFloat(item.quantity) || 0;
        const price = parseFloat(item.unitPrice) || 0;
        return sum + qty * price;
    }, 0);

    const remainingBalance = totalPrice - (parseFloat(paidPrice) || 0);

    // ── Load reference data ───────────────────────────────────────────────────
    const loadRefData = useCallback(async () => {
        setRefLoading(true);
        try {
            const [suppRes, stockRes] = await Promise.all([
                supplierAPI.getAll({ limit: 100, isActive: true }),
                centralStockAPI.getAll({ limit: 100 }),
            ]);
            setSuppliers(suppRes.data?.data || []);
            setStockItems(stockRes.data?.data || []);
        } catch {
            // silent — user sees empty dropdowns
        } finally {
            setRefLoading(false);
        }
    }, []);

    // ── Populate form on open ─────────────────────────────────────────────────
    useEffect(() => {
        if (open) {
            loadRefData();
            if (order) {
                setSupplier(order.supplier || null);
                setItems(
                    (order.items || []).map(i => ({
                        centralStockItem: i.centralStockItem || null,
                        quantity: String(i.quantity || ''),
                        unitPrice: String(i.unitPrice || ''),
                    }))
                );
                setPaidPrice(String(order.paidPrice || ''));
                setNotes(order.notes || '');
            } else {
                setSupplier(null);
                setItems([emptyItem()]);
                setPaidPrice('');
                setNotes('');
            }
            setErrors({});
        }
    }, [open, order, loadRefData]);

    // ── Item helpers ──────────────────────────────────────────────────────────
    const handleItemChange = (index, field, value) => {
        setItems(prev => {
            const next = [...prev];
            next[index] = { ...next[index], [field]: value };
            return next;
        });
        // Clear related error
        const errKey = field === 'centralStockItem'
            ? `item_${index}_stock`
            : field === 'quantity'
                ? `item_${index}_qty`
                : `item_${index}_price`;
        if (errors[errKey]) setErrors(p => ({ ...p, [errKey]: '' }));
    };

    const addItem = () => setItems(prev => [...prev, emptyItem()]);
    const removeItem = (index) => setItems(prev => prev.filter((_, i) => i !== index));

    // ── Validation ────────────────────────────────────────────────────────────
    const validate = () => {
        const errs = {};

        if (!supplier) errs.supplier = t('orderList.supplierRequired');

        if (items.length === 0) {
            errs.items = t('orderList.itemsRequired');
        } else {
            items.forEach((item, i) => {
                if (!item.centralStockItem)
                    errs[`item_${i}_stock`] = t('orderList.stockItemRequired');
                if (!item.quantity || parseFloat(item.quantity) < 1)
                    errs[`item_${i}_qty`] = t('orderList.quantityRequired');
                if (item.unitPrice === '' || parseFloat(item.unitPrice) < 0)
                    errs[`item_${i}_price`] = t('orderList.unitPriceRequired');
            });
        }

        const paid = parseFloat(paidPrice) || 0;
        if (paid < 0 || paid > totalPrice) errs.paidPrice = t('common.invalid');

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    // ── Submit ────────────────────────────────────────────────────────────────
    const handleSubmit = async () => {
        if (!validate()) return;
        setLoading(true);
        try {
            const payload = {
                supplier: supplier._id,
                items: items.map(item => ({
                    centralStockItem: item.centralStockItem._id,
                    quantity: parseInt(item.quantity, 10),
                    unitPrice: parseFloat(item.unitPrice),
                })),
                paidPrice: parseFloat(paidPrice) || 0,
                notes: notes.trim(),
            };

            if (isEdit) {
                await orderListAPI.update(order._id, payload);
                toast.success(t('orderList.updateSuccess'));
            } else {
                await orderListAPI.create(payload);
                toast.success(t('orderList.createSuccess'));
            }
            onSuccess();
        } catch (err) {
            const msg = err.response?.data?.message || t('orderList.saveError');
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>
                {isEdit ? t('orderList.editTitle') : t('orderList.addTitle')}
            </DialogTitle>

            <DialogContent dividers>
                {refLoading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box display="flex" flexDirection="column" gap={2} pt={1}>

                        {/* ── Supplier ───────────────────────────────────── */}
                        <Autocomplete
                            options={suppliers}
                            getOptionLabel={(o) => o.name || ''}
                            isOptionEqualToValue={(o, v) => o._id === v._id}
                            value={supplier}
                            onChange={(_, v) => {
                                setSupplier(v);
                                if (errors.supplier) setErrors(p => ({ ...p, supplier: '' }));
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t('orderList.supplier')}
                                    required
                                    size="small"
                                    error={Boolean(errors.supplier)}
                                    helperText={errors.supplier}
                                />
                            )}
                        />

                        <Divider />

                        {/* ── Items ──────────────────────────────────────── */}
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle2" fontWeight={600}>
                                {t('orderList.items')}
                            </Typography>
                            <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={addItem}
                                variant="outlined"
                            >
                                {t('orderList.addItem')}
                            </Button>
                        </Box>

                        {errors.items && (
                            <Typography color="error" variant="caption">
                                {errors.items}
                            </Typography>
                        )}

                        {items.map((item, index) => (
                            <Paper
                                key={index}
                                variant="outlined"
                                sx={{ p: 1.5 }}
                            >
                                <Grid container spacing={1.5} alignItems="flex-start">

                                    {/* Stock Item */}
                                    <Grid item xs={12} sm={5}>
                                        <Autocomplete
                                            options={stockItems}
                                            getOptionLabel={(o) => o.name || ''}
                                            isOptionEqualToValue={(o, v) => o._id === v._id}
                                            value={item.centralStockItem}
                                            onChange={(_, v) =>
                                                handleItemChange(index, 'centralStockItem', v)
                                            }
                                            renderOption={(props, option) => (
                                                <li {...props} key={option._id}>
                                                    <Box>
                                                        <Typography variant="body2">
                                                            {option.name}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            {t('common.quantity')}: {option.quantity}{' '}
                                                            {option.unit}
                                                            {' | '}
                                                            {t('centralStock.avgCost') || 'Avg Cost'}:{' '}
                                                            {option.averageCost?.toFixed(2) || 0}{' '}
                                                            {t('common.currency')}
                                                        </Typography>
                                                    </Box>
                                                </li>
                                            )}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={t('orderList.selectStockItem')}
                                                    size="small"
                                                    required
                                                    error={Boolean(errors[`item_${index}_stock`])}
                                                    helperText={errors[`item_${index}_stock`]}
                                                />
                                            )}
                                        />
                                    </Grid>

                                    {/* Quantity */}
                                    <Grid item xs={6} sm={2}>
                                        <TextField
                                            label={t('orderList.quantity')}
                                            value={item.quantity}
                                            onChange={(e) =>
                                                handleItemChange(index, 'quantity', e.target.value)
                                            }
                                            size="small"
                                            fullWidth
                                            type="number"
                                            inputProps={{ min: 1 }}
                                            required
                                            error={Boolean(errors[`item_${index}_qty`])}
                                            helperText={errors[`item_${index}_qty`]}
                                        />
                                    </Grid>

                                    {/* Unit Price */}
                                    <Grid item xs={6} sm={3}>
                                        <TextField
                                            label={t('orderList.unitPrice')}
                                            value={item.unitPrice}
                                            onChange={(e) =>
                                                handleItemChange(index, 'unitPrice', e.target.value)
                                            }
                                            size="small"
                                            fullWidth
                                            type="number"
                                            inputProps={{ min: 0, step: 0.01 }}
                                            required
                                            error={Boolean(errors[`item_${index}_price`])}
                                            helperText={errors[`item_${index}_price`]}
                                        />
                                    </Grid>

                                    {/* Line Total + Remove */}
                                    <Grid
                                        item xs={12} sm={2}
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                    >
                                        <Typography variant="body2" fontWeight={500}>
                                            {(
                                                (parseFloat(item.quantity) || 0) *
                                                (parseFloat(item.unitPrice) || 0)
                                            ).toFixed(2)}{' '}
                                            {t('common.currency')}
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => removeItem(index)}
                                            disabled={items.length === 1}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            </Paper>
                        ))}

                        <Divider />

                        {/* ── Financials ─────────────────────────────────── */}
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label={t('orderList.totalPrice')}
                                    value={totalPrice.toFixed(2)}
                                    size="small"
                                    fullWidth
                                    disabled
                                    InputProps={{
                                        endAdornment: (
                                            <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                                                {t('common.currency')}
                                            </Typography>
                                        )
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label={t('orderList.paidPrice')}
                                    value={paidPrice}
                                    onChange={(e) => {
                                        setPaidPrice(e.target.value);
                                        if (errors.paidPrice)
                                            setErrors(p => ({ ...p, paidPrice: '' }));
                                    }}
                                    size="small"
                                    fullWidth
                                    type="number"
                                    inputProps={{ min: 0, step: 0.01 }}
                                    error={Boolean(errors.paidPrice)}
                                    helperText={errors.paidPrice}
                                    InputProps={{
                                        endAdornment: (
                                            <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                                                {t('common.currency')}
                                            </Typography>
                                        )
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label={t('orderList.remainingBalance')}
                                    value={remainingBalance.toFixed(2)}
                                    size="small"
                                    fullWidth
                                    disabled
                                    InputProps={{
                                        endAdornment: (
                                            <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                                                {t('common.currency')}
                                            </Typography>
                                        ),
                                        sx: {
                                            color: remainingBalance > 0
                                                ? 'error.main'
                                                : 'success.main'
                                        }
                                    }}
                                />
                            </Grid>
                        </Grid>

                        {/* ── Notes ─────────────────────────────────────── */}
                        <TextField
                            label={t('orderList.notes')}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            size="small"
                            fullWidth
                            multiline
                            rows={2}
                        />
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    {t('common.cancel')}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading || refLoading}
                    startIcon={loading ? <CircularProgress size={16} /> : null}
                >
                    {loading ? t('common.processing') : t('common.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default OrderFormDialog;